/**
 * OneAmerica AssetCare Pricing Utilities
 * Shared types, interpolation logic, and lookup functions used by
 * both category-1 flat bands and category-3 demographic-aware bands.
 */

import { FundingPolicyType } from "@shared";
import { OA_CAT2_DEMO_BANDS, OA_CAT2_AVAILABLE_AGES, OA_CAT2_MIXED_MIN_AGES, OA_CAT2_MIXED_MAX_AGES } from "./oneAmericaCat2DemoBands";
import { OA_CAT3_DEMO_BANDS, OA_CAT3_AVAILABLE_AGES } from "./oneAmericaCat3DemoBands";
import { OA_BANDS_BY_CATEGORY } from "./oneAmericaPricingConfig";

export type BandEntry = { premium: number; db: number; ltc: number };
export type ConfigBands = Record<string, BandEntry[]>;

/**
 * Why a band lookup could not price a premium. The cases need different handling, so they are
 * distinguished rather than collapsed into one "out of range" flag:
 *
 * - 'above-band': a table applies, but the premium is above its last row. Flat-clamping the
 *   edge multiplier here is what let benefits scale linearly to any premium while ROI stayed
 *   constant (the A2 defect), so callers must refuse to reprice.
 * - 'no-band': no table exists for this state category, product, config, or demographic. The
 *   multipliers are inert 1.0s, which yields plain linear scaling — the prior behavior for
 *   these rows, and bounded now that the premium is always clamped to known bounds.
 *
 * Premiums *below* the first row are deliberately not a miss: they keep the flat clamp. That
 * direction shrinks benefits rather than inflating them, is already floored by investmentMin,
 * and is the common real case — measured across the quote table, 0 OneAmerica rows sit above
 * their table's last band at their own premium while 44.4% of category-3 rows sit below the
 * first. Refusing those would have stranded ~37k quotes for no safety gain.
 */
export type OABandMiss = 'above-band' | 'no-band';

export type OAMultipliers = { db: number; ltc: number; miss?: OABandMiss };

const OA_MULTIPLIER_FALLBACK: OAMultipliers = { db: 1.0, ltc: 1.0, miss: 'no-band' };

function combineMiss(a: OAMultipliers, b: OAMultipliers): OABandMiss | undefined {
  if (a.miss === 'above-band' || b.miss === 'above-band') return 'above-band';
  return a.miss ?? b.miss;
}

export function interpolateBand(bands: BandEntry[], premium: number): OAMultipliers {
  if (bands.length === 0) return OA_MULTIPLIER_FALLBACK;

  const first = bands[0];
  const last = bands[bands.length - 1];

  if (premium > last.premium) return { db: last.db, ltc: last.ltc, miss: 'above-band' };
  if (premium <= first.premium) return { db: first.db, ltc: first.ltc };
  if (premium === last.premium) return { db: last.db, ltc: last.ltc };

  for (let i = 0; i < bands.length - 1; i++) {
    if (premium >= bands[i].premium && premium < bands[i + 1].premium) {
      const t = (premium - bands[i].premium) / (bands[i + 1].premium - bands[i].premium);
      return {
        db: bands[i].db + t * (bands[i + 1].db - bands[i].db),
        ltc: bands[i].ltc + t * (bands[i + 1].ltc - bands[i].ltc),
      };
    }
  }

  return OA_MULTIPLIER_FALLBACK;
}

const OA_ASSET_CARE_TYPES = new Set([
  FundingPolicyType.assetCare,
  FundingPolicyType.jointAssetCare,
]);

const OA_JOINT_TYPES = new Set([
  FundingPolicyType.jointAssetCare,
]);

export function isOneAmericaAssetCare(fundingPolicyType: number): boolean {
  return OA_ASSET_CARE_TYPES.has(fundingPolicyType);
}

function normalizeInflationValue(value: string | undefined | null): string {
  if (!value || value === "null" || value.trim() === "" || value.trim() === "0%" || value.trim() === "0") return "None";
  return value;
}

export function resolveProductKey(
  fundingPolicyType: number,
  productFundingOption: string | undefined,
): string {
  const isJoint = OA_JOINT_TYPES.has(fundingPolicyType);
  const is10Pay =
    productFundingOption?.toLowerCase().includes("recurring") ||
    productFundingOption?.toLowerCase().includes("10");
  if (is10Pay) return isJoint ? "10pay_joint" : "10pay_single";
  return isJoint ? "sp_joint" : "sp_single";
}

export function buildConfigKey(
  aobInflation: string | undefined | null,
  cobInflation: string | undefined | null,
  cobDuration: string | undefined | null,
): string {
  const aob = normalizeInflationValue(aobInflation);
  const cob = normalizeInflationValue(cobInflation);
  const dur = cobDuration?.trim() || "2 Years";
  return `${aob}|${cob}|${dur}`;
}

export function getOAMultipliersForPremium(
  fundingPolicyType: number,
  productFundingOption: string | undefined,
  aobInflation: string | undefined | null,
  cobInflation: string | undefined | null,
  cobDuration: string | undefined | null,
  premium: number,
  usaStateCategory: number | undefined,
  primaryAge?: number,
  primaryGender?: string,
  aobDuration?: string | undefined | null,
  secondaryAge?: number,
): OAMultipliers {
  const productKey = resolveProductKey(fundingPolicyType, productFundingOption);
  const isJoint = OA_JOINT_TYPES.has(fundingPolicyType);

  if (usaStateCategory === 3 && primaryAge && primaryGender) {
    const configKey = buildConfigKey(aobInflation, cobInflation, cobDuration);
    return getCat3DemoMultipliers(productKey, primaryGender, primaryAge, configKey, premium);
  }

  if (usaStateCategory === 2 && primaryAge) {
    const aob = normalizeInflationValue(aobInflation);
    const cob = normalizeInflationValue(cobInflation);
    const aobDur = aobDuration?.trim() || "";
    const cobDur = cobDuration?.trim() || "";
    const configKey = `${aob}|${cob}|${aobDur}|${cobDur}`;
    return getCat2DemoMultipliers(productKey, primaryAge, configKey, premium, isJoint ? secondaryAge : undefined);
  }

  const configKey = buildConfigKey(aobInflation, cobInflation, cobDuration);
  const categoryBands = usaStateCategory ? OA_BANDS_BY_CATEGORY[usaStateCategory] : undefined;
  if (!categoryBands) return OA_MULTIPLIER_FALLBACK;

  const configBands = categoryBands[productKey];
  if (!configBands) return OA_MULTIPLIER_FALLBACK;

  const bands = configBands[configKey];
  if (!bands) return OA_MULTIPLIER_FALLBACK;

  return interpolateBand(bands, premium);
}

function findBracket(age: number, ages: readonly number[]): [number, number] {
  if (age <= ages[0]) return [ages[0], ages[0]];
  if (age >= ages[ages.length - 1]) return [ages[ages.length - 1], ages[ages.length - 1]];
  for (let i = 0; i < ages.length - 1; i++) {
    if (age >= ages[i] && age <= ages[i + 1]) return [ages[i], ages[i + 1]];
  }
  return [ages[0], ages[ages.length - 1]];
}

function lerp(a: OAMultipliers, b: OAMultipliers, t: number): OAMultipliers {
  return {
    db: a.db + t * (b.db - a.db),
    ltc: a.ltc + t * (b.ltc - a.ltc),
    miss: combineMiss(a, b),
  };
}

function lookupSameAge(
  productKey: string, age: number, configKey: string, premium: number,
): OAMultipliers {
  const exactKey = `${productKey}|${age}|${configKey}`;
  const exactBands = OA_CAT2_DEMO_BANDS[exactKey];
  if (exactBands) return interpolateBand(exactBands, premium);

  const [lo, hi] = findBracket(age, OA_CAT2_AVAILABLE_AGES);
  if (lo === hi) {
    const bands = OA_CAT2_DEMO_BANDS[`${productKey}|${lo}|${configKey}`];
    return bands ? interpolateBand(bands, premium) : OA_MULTIPLIER_FALLBACK;
  }
  const loBands = OA_CAT2_DEMO_BANDS[`${productKey}|${lo}|${configKey}`];
  const hiBands = OA_CAT2_DEMO_BANDS[`${productKey}|${hi}|${configKey}`];
  if (!loBands && !hiBands) return OA_MULTIPLIER_FALLBACK;
  if (!loBands) return interpolateBand(hiBands!, premium);
  if (!hiBands) return interpolateBand(loBands, premium);
  const t = (age - lo) / (hi - lo);
  return lerp(interpolateBand(loBands, premium), interpolateBand(hiBands, premium), t);
}

function lookupMixedAge(
  productKey: string, minAge: number, maxAge: number, configKey: string, premium: number,
): OAMultipliers | null {
  const exactKey = `${productKey}|${minAge}|${maxAge}|${configKey}`;
  const exactBands = OA_CAT2_DEMO_BANDS[exactKey];
  if (exactBands) return interpolateBand(exactBands, premium);

  const [minLo, minHi] = findBracket(minAge, OA_CAT2_MIXED_MIN_AGES);
  const [maxLo, maxHi] = findBracket(maxAge, OA_CAT2_MIXED_MAX_AGES);

  const corners: (OAMultipliers | null)[] = [
    OA_CAT2_DEMO_BANDS[`${productKey}|${minLo}|${maxLo}|${configKey}`],
    OA_CAT2_DEMO_BANDS[`${productKey}|${minLo}|${maxHi}|${configKey}`],
    OA_CAT2_DEMO_BANDS[`${productKey}|${minHi}|${maxLo}|${configKey}`],
    OA_CAT2_DEMO_BANDS[`${productKey}|${minHi}|${maxHi}|${configKey}`],
  ].map(b => b ? interpolateBand(b, premium) : null);

  const available = corners.filter((c): c is OAMultipliers => c !== null);
  if (available.length === 0) return null;
  if (available.length === 1) return available[0];

  if (available.length === 4) {
    const tMin = minLo === minHi ? 0 : (minAge - minLo) / (minHi - minLo);
    const tMax = maxLo === maxHi ? 0 : (maxAge - maxLo) / (maxHi - maxLo);
    const bot = lerp(corners[0]!, corners[1]!, tMax);
    const top = lerp(corners[2]!, corners[3]!, tMax);
    return lerp(bot, top, tMin);
  }

  const sum = available.reduce((a, c) => ({ db: a.db + c.db, ltc: a.ltc + c.ltc }), { db: 0, ltc: 0 });
  return {
    db: sum.db / available.length,
    ltc: sum.ltc / available.length,
    miss: available.reduce<OABandMiss | undefined>(
      (acc, corner) => combineMiss({ db: 0, ltc: 0, miss: acc }, corner),
      undefined,
    ),
  };
}

export function getCat2DemoMultipliers(
  productKey: string,
  primaryAge: number,
  configKey: string,
  premium: number,
  secondaryAge?: number,
): OAMultipliers {
  const isJoint = productKey.includes('joint');
  const secAge = secondaryAge ?? primaryAge;

  if (isJoint && secAge !== primaryAge) {
    const minAge = Math.min(primaryAge, secAge);
    const maxAge = Math.max(primaryAge, secAge);
    const mixedResult = lookupMixedAge(productKey, minAge, maxAge, configKey, premium);
    if (mixedResult) return mixedResult;
    return lookupSameAge(productKey, maxAge, configKey, premium);
  }

  return lookupSameAge(productKey, primaryAge, configKey, premium);
}

function findBracketAges(age: number): [number, number] {
  const ages = OA_CAT3_AVAILABLE_AGES;
  if (age <= ages[0]) return [ages[0], ages[0]];
  if (age >= ages[ages.length - 1]) return [ages[ages.length - 1], ages[ages.length - 1]];
  for (let i = 0; i < ages.length - 1; i++) {
    if (age >= ages[i] && age <= ages[i + 1]) return [ages[i], ages[i + 1]];
  }
  return [ages[0], ages[ages.length - 1]];
}

export function getCat3DemoMultipliers(
  productKey: string,
  gender: string,
  age: number,
  configKey: string,
  premium: number,
): OAMultipliers {
  const normalizedGender = gender === 'Female' ? 'Female' : 'Male';

  const exactKey = `${productKey}|${normalizedGender}|${age}|${configKey}`;
  const exactBands = OA_CAT3_DEMO_BANDS[exactKey];
  if (exactBands) return interpolateBand(exactBands, premium);

  const [ageLow, ageHigh] = findBracketAges(age);
  if (ageLow === ageHigh) {
    const key = `${productKey}|${normalizedGender}|${ageLow}|${configKey}`;
    const bands = OA_CAT3_DEMO_BANDS[key];
    return bands ? interpolateBand(bands, premium) : OA_MULTIPLIER_FALLBACK;
  }

  const lowKey = `${productKey}|${normalizedGender}|${ageLow}|${configKey}`;
  const highKey = `${productKey}|${normalizedGender}|${ageHigh}|${configKey}`;
  const lowBands = OA_CAT3_DEMO_BANDS[lowKey];
  const highBands = OA_CAT3_DEMO_BANDS[highKey];

  if (!lowBands && !highBands) return OA_MULTIPLIER_FALLBACK;
  if (!lowBands) return interpolateBand(highBands!, premium);
  if (!highBands) return interpolateBand(lowBands, premium);

  const lowResult = interpolateBand(lowBands, premium);
  const highResult = interpolateBand(highBands, premium);
  const t = (age - ageLow) / (ageHigh - ageLow);
  return {
    db: lowResult.db + t * (highResult.db - lowResult.db),
    ltc: lowResult.ltc + t * (highResult.ltc - lowResult.ltc),
    miss: combineMiss(lowResult, highResult),
  };
}
